import { useEffect, useState } from "react";
import MobileLayout from "@/components/MobileLayout";
import { Phone, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function ContactPage() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);
  const phone = settings?.contact_phone || "944447450";
  const email = settings?.contact_email || "infokapei2@gmail.com";
  return (
    <MobileLayout>
      <h1 className="mb-6 text-4xl font-black tracking-tight text-slate-900">Contacto</h1>
      <div className="flex flex-col gap-4">
        <a
          data-testid="contact-phone"
          href={`tel:${phone}`}
          className="kapei-card flex items-center gap-4 hover:bg-slate-100"
        >
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#4d7c0f] text-white">
            <Phone className="h-6 w-6" strokeWidth={3} />
          </div>
          <div>
            <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Teléfono</div>
            <div className="text-xl font-black text-slate-900">{phone}</div>
          </div>
        </a>
        <a
          data-testid="contact-email"
          href={`mailto:${email}`}
          className="kapei-card flex items-center gap-4 hover:bg-slate-100"
        >
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#4d7c0f] text-white">
            <Mail className="h-6 w-6" strokeWidth={3} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Email</div>
            <div className="truncate text-lg font-black text-slate-900">{email}</div>
          </div>
        </a>
        {settings?.address && (
          <div className="kapei-card flex items-start gap-4">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[#4d7c0f] text-white">
              <MapPin className="h-6 w-6" strokeWidth={3} />
            </div>
            <div>
              <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Dirección</div>
              <div className="text-base font-bold text-slate-900">{settings.address}</div>
              <div className="mt-1 text-sm text-slate-600">{settings.business_hours}</div>
            </div>
          </div>
        )}
      </div>
    </MobileLayout>
  );
}

export function HelpPage() {
  return (
    <MobileLayout>
      <h1 className="mb-6 text-4xl font-black tracking-tight text-slate-900">Cómo enviar archivos</h1>
      <ol className="flex flex-col gap-4">
        {[
          "Abre tu archivo (PDF, foto, documento…).",
          "Pulsa Compartir, o entra a KAPEI desde aquí.",
          "Elige “Envío rápido”.",
          "Pulsa “Enviar a KAPEI”.",
          "Indica el código KP-XXXX en mostrador.",
        ].map((step, i) => (
          <li key={step} className="flex items-start gap-4 rounded-2xl border-2 border-slate-200 bg-white p-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#4d7c0f] text-2xl font-black text-white">
              {i + 1}
            </div>
            <p className="pt-2 text-base font-bold text-slate-900">{step}</p>
          </li>
        ))}
      </ol>
      <div id="privacidad" className="mt-10 rounded-2xl border-2 border-slate-200 bg-slate-50 p-5">
        <h2 className="text-xl font-extrabold text-slate-900">Privacidad</h2>
        <p className="mt-2 text-sm text-slate-700">
          Tus archivos se almacenan temporalmente solo para procesar tu pedido de impresión.
          Se eliminan automáticamente tras el periodo configurado por KAPEI. Finalidad:
          gestión de pedidos. No compartimos tus datos con terceros.
        </p>
      </div>
    </MobileLayout>
  );
}

export function QuotePage() {
  const [body, setBody] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!body.trim()) return;
    setLoading(true);
    try {
      await api.post("/messages", { body, kind: "quote" });
      setDone(true);
      toast.success("Solicitud enviada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <MobileLayout>
      <h1 className="mb-2 text-4xl font-black tracking-tight text-slate-900">Pedir presupuesto</h1>
      <p className="mb-6 text-base text-slate-600">Cuéntanos qué necesitas y te respondemos.</p>

      {done ? (
        <div className="kapei-card text-center" data-testid="quote-success">
          <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-[#4d7c0f]" />
          <p className="font-bold text-slate-900">Solicitud enviada</p>
          <p className="mt-1 text-sm text-slate-600">Te contactaremos pronto.</p>
        </div>
      ) : (
        <>
          <Textarea
            data-testid="quote-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Ej: 50 folletos a color, doble cara, A5, en cartulina…"
            className="min-h-[160px] rounded-xl border-2 border-slate-300 p-4 text-base"
          />
          <Button
            data-testid="quote-submit"
            disabled={loading}
            onClick={submit}
            className="kapei-btn-huge mt-4 bg-[#4d7c0f] text-white hover:bg-[#3f6212]"
          >
            <Send className="mr-2 h-5 w-5" strokeWidth={3} />
            Enviar solicitud
          </Button>
        </>
      )}
    </MobileLayout>
  );
}
